/**
 * WEB-939 — Discover v3 no-match empty copy + widen decision.
 *
 * The v3 empty frame hard-codes a two-filter prototype pair. That pair is a
 * fixture, not a measured claim about the catalogue. This module names the
 * live non-date filters (at most two; else `{n} filters`) and offers a widen
 * that is strictly broader than the current date scope.
 *
 * Upcoming-default decision (AC step 3): hide Widen when `date` is unset.
 * `nextWidenDatePreset(undefined)` returns `next7`, but absent `date` is an
 * unbounded upcoming window server-side (`browseNotOverWhere(undefined)` has
 * no `lte`; `buildDiscoverQueryArgs` omits `date` when `mapDateKeyword`
 * returns `undefined`). Emitting `next7` (a bounded 168-hour window) would
 * *narrow* that query. The list tail still uses `nextWidenDatePreset` as-is — same
 * defect, noted on WEB-939 rather than silently copied here.
 */
import { PRICE_PRESET_LABELS, isPricePreset } from '@/lib/discover/pricePreset';

import { deriveActiveFilterDescriptors } from './activeFilterCount';
import {
  formatCategoryLabel,
  formatNeighborhoodLabel,
  formatSearchLabel,
} from './activeFilterLabels';
import {
  formatDiscoverListTailScope,
  formatDiscoverListTailWidenLabel,
  nextWidenDatePreset,
} from './listTailCopy';
import { defaultDiscoverParams, type DateValue, type DiscoverParams } from './url-state';

const NO_MATCH_HINT = 'Try widening the date or removing a filter.';
const NO_WIDEN_HINT = 'Try removing a filter.';
const FOLLOWED_ONLY_LABEL = 'Only who I follow';
const UNNAMED_VENUE_LABEL = 'Venue';
const MAX_NAMED_FILTERS = 2;

const BARE_SCOPE_NOUNS = new Set(['tonight', 'tomorrow', 'this weekend', 'this week', 'upcoming']);

export interface NoMatchEmptyCopyOptions {
  readonly venueNames?: ReadonlyMap<string, string>;
}

export interface NoMatchEmptyCopy {
  readonly mode: 'date-only' | 'named' | 'count';
  readonly headline: string;
  readonly namedLabels: readonly string[];
  readonly filterCount: number;
  readonly scope: string;
  readonly hint: string | null;
  readonly widenPreset: 'next7' | 'next30' | null;
  readonly widenLabel: string | null;
}

/**
 * Next date preset that is strictly wider than the current scope, or `null`.
 * Unset `date` (upcoming default) is already unbounded — do not offer `next7`.
 */
export function nextEmptyStateWidenDatePreset(
  date: DateValue | undefined,
): 'next7' | 'next30' | null {
  if (date === undefined) return null;
  return nextWidenDatePreset(date);
}

export function formatEmptyStateWidenLabel(nextPreset: 'next7' | 'next30'): string {
  return `Widen to ${formatDiscoverListTailWidenLabel(nextPreset).toLowerCase()}`;
}

/**
 * Apply the empty-state widen: set `date` only. Returns `null` when no
 * strictly wider preset exists.
 */
export function applyEmptyStateWiden(params: DiscoverParams): DiscoverParams | null {
  const nextDate = nextEmptyStateWidenDatePreset(params.date);
  if (nextDate === null) return null;
  return { ...params, date: nextDate };
}

function labelForNonDateFilter(
  params: DiscoverParams,
  descriptor: ReturnType<typeof deriveActiveFilterDescriptors>[number],
  options: NoMatchEmptyCopyOptions,
): string | null {
  const { facet } = descriptor;
  switch (facet) {
    case 'date':
      return null;
    case 'category':
      return formatCategoryLabel(descriptor.value ?? '');
    case 'neighborhood':
      return formatNeighborhoodLabel(descriptor.value ?? '');
    case 'venue':
      return options.venueNames?.get(descriptor.value ?? '') ?? UNNAMED_VENUE_LABEL;
    case 'price': {
      const price = params.price;
      if (price === undefined || !isPricePreset(price)) return null;
      return PRICE_PRESET_LABELS[price];
    }
    case 'q':
      return formatSearchLabel(params.q ?? '');
    case 'followedOnly':
      return FOLLOWED_ONLY_LABEL;
    default: {
      const _exhaustive: never = facet;
      return _exhaustive;
    }
  }
}

function collectNonDateFilterLabels(
  params: DiscoverParams,
  options: NoMatchEmptyCopyOptions,
): string[] {
  const labels: string[] = [];
  for (const descriptor of deriveActiveFilterDescriptors(params)) {
    const label = labelForNonDateFilter(params, descriptor, options);
    if (label === null) continue;
    labels.push(label);
  }
  return labels;
}

function formatDateOnlyHeadline(scope: string): string {
  if (BARE_SCOPE_NOUNS.has(scope)) {
    return `Nothing ${scope} yet.`;
  }
  return `Nothing in ${scope} yet.`;
}

function joinNamedLabels(labels: readonly string[]): string {
  return labels.join(' + ');
}

function hintForFilters(widenPreset: 'next7' | 'next30' | null): string {
  return widenPreset === null ? NO_WIDEN_HINT : NO_MATCH_HINT;
}

/**
 * Pure no-match copy from {@link DiscoverParams}. Optional genre/venue maps
 * only name those chips; the function is well-defined without them.
 */
export function buildNoMatchEmptyCopy(
  params: DiscoverParams,
  options: NoMatchEmptyCopyOptions = {},
): NoMatchEmptyCopy {
  const scope = formatDiscoverListTailScope(params.date);
  const labels = collectNonDateFilterLabels(params, options);
  const filterCount = labels.length;
  const widenPreset = nextEmptyStateWidenDatePreset(params.date);
  const widenLabel = widenPreset === null ? null : formatEmptyStateWidenLabel(widenPreset);

  if (filterCount === 0) {
    return {
      mode: 'date-only',
      headline: formatDateOnlyHeadline(scope),
      namedLabels: [],
      filterCount,
      scope,
      hint: null,
      widenPreset,
      widenLabel,
    };
  }

  if (filterCount > MAX_NAMED_FILTERS) {
    return {
      mode: 'count',
      headline: `No events match ${filterCount} filters ${scope}.`,
      namedLabels: [],
      filterCount,
      scope,
      hint: hintForFilters(widenPreset),
      widenPreset,
      widenLabel,
    };
  }

  return {
    mode: 'named',
    headline: `No events match ${joinNamedLabels(labels)} ${scope}.`,
    namedLabels: labels,
    filterCount,
    scope,
    hint: hintForFilters(widenPreset),
    widenPreset,
    widenLabel,
  };
}

/** Default Discover URL state, for stories/tests that only have a `date`. */
export function noMatchParamsFromDate(date: DateValue | undefined): DiscoverParams {
  return date === undefined ? defaultDiscoverParams() : { ...defaultDiscoverParams(), date };
}

export { NO_MATCH_HINT, NO_WIDEN_HINT, UNNAMED_VENUE_LABEL };
